import { forwardRef, useEffect, useState } from 'react'
import { Input, InputProps } from '@/components/ui/input'

export const formatCPF = (value: string = '') => {
  const digits = value.replace(/\D/g, '').slice(0, 11)
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
}

type CPFInputProps = Omit<InputProps, 'value' | 'onChange'> & {
  value?: string
  onCPFChange?: (value: string, isValid: boolean) => void
}

const CPFInput = forwardRef<HTMLInputElement, CPFInputProps>(({ value = '', onCPFChange, ...props }, ref) => {
  const [raw, setRaw] = useState(value)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => setRaw(value), [value])

  const handleBlur = () => {
    const isValid = validaCPF(raw)
    setError(isValid ? null : 'CPF inválido. Verifique o número digitado.')
    onCPFChange?.(raw, isValid)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 11)
    setRaw(digits)
    setError(null) // limpa erro visual
    onCPFChange?.(digits, false) // envia valor parcial com `isValid=false`
  }

  return (
    <div className="space-y-1">
      <Input
        {...props}
        ref={ref}
        type="text"
        inputMode="numeric"
        pattern="\d{3}\.\d{3}\.\d{3}-\d{2}"
        placeholder="000.000.000-00"
        maxLength={14}
        value={formatCPF(raw)}
        onChange={handleChange}
        onBlur={handleBlur}
        className={`bg-input text-foreground ${error ? 'border-red-500' : ''}`}
      />
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  )
})

export function validaCPF(cpf: string): boolean {
  cpf = cpf.replace(/\D/g, '')
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false

  let soma = 0
  for (let i = 0; i < 9; i++) soma += parseInt(cpf.charAt(i)) * (10 - i)
  let resto = (soma * 10) % 11
  if (resto === 10 || resto === 11) resto = 0
  if (resto !== parseInt(cpf.charAt(9))) return false

  soma = 0
  for (let i = 0; i < 10; i++) soma += parseInt(cpf.charAt(i)) * (11 - i)
  resto = (soma * 10) % 11
  if (resto === 10 || resto === 11) resto = 0
  if (resto !== parseInt(cpf.charAt(10))) return false

  return true
}

export default CPFInput
